import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useMutation } from '@tanstack/react-query';
import { Dialog } from '@/components-new/Dialog/Dialog';
import { DialogButtons } from '@/components-new/Dialog/styles';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { FormikSelectableTabs } from '@/components/form/fields/FormikSelectableTabs/FormikSelectableTabs';
import { FormikRadioGroup } from '@/components/form/fields/FormikRadioGroup/FormikRadioGroup';
import { FormikSwitchToggle } from '@/components/form/fields/FormikSwitchToggle/FormikSwitchToggle';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { copyToClipboard, showSnackbarResponseError } from '@/helpers/helpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import {
  RecipientRequestType,
  RequestModeType,
  RequestTypeType,
  VendorVaultCatalogType,
} from '@/types/vendorVaultTypes';
import { CreateRecipientRequestPayloadType } from '@/api/v1/organization/vendor-vault/createRecipientRequestApi';
import { OptionGrid } from '@/bundle/_OrgAdmin/pages/MyVaultPage/styles';
import { createRecipientRequest } from '../../api/api';
import { REQUEST_EXPIRY_OPTIONS, REQUEST_VIA_LABEL } from '../../const/const';
import { NewRequestFormType, newRequestInitialValues, newRequestValidationSchema } from './const';
import { FormikChips } from '@/components/form/fields/FormikChips/FormikChips';
import { HighlightPanel } from '@/components-new/HighlightPanel/styles';
import { COLORS } from '@/styles/colors';
import { IconButton } from '@/components/IconButton/IconButton';
import { FaCopy } from 'react-icons/fa';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { Mono } from '@/styles/common';
import {
  getBankOptions,
  getRequestTypeOptions,
  getTemplateFields,
  getTemplateLabel,
  isDraftTemplate,
} from '../../helpers/catalogHelpers';

type NewRequestModalType = {
  isOpen: boolean;
  catalog: VendorVaultCatalogType;
  requestType: RequestTypeType;
  initialMode: RequestModeType;
  onClose: () => void;
  onSuccess: () => void;
};

export const NewRequestModal = ({
  isOpen,
  catalog,
  requestType,
  initialMode,
  onClose,
  onSuccess,
}: NewRequestModalType) => {
  const [sent, setSent] = useState<RecipientRequestType | null>(null);

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['create_recipient_request'],
    mutationFn: (payload: CreateRecipientRequestPayloadType) => createRecipientRequest(payload),
    onSuccess(response) {
      if (response?.error) return showSnackbarResponseError(response.error);
      amplitudeService.logEvent(AMPLITUDE_EVENTS.VendorVaultRequestSuccess);
      setSent(response.body);
      onSuccess();
    },
  });

  const formik = useFormik<NewRequestFormType>({
    initialValues: newRequestInitialValues(requestType, initialMode),
    validationSchema: newRequestValidationSchema,
    onSubmit: (values) =>
      mutate({
        ...values,
        recipient_name: values.recipient_name.trim(),
        sent_to: values.sent_to.trim(),
        bank: values.request_type === 'intl' ? values.bank : null,
      }),
  });

  useEffect(() => {
    formik.setFieldValue('sent_to', '');
    formik.setFieldTouched('sent_to', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.via]);

  const copy = async () => {
    copyToClipboard(sent.url, 'Link successfully copied to clipboard');
  };

  const { values } = formik;
  const isIntl = values.request_type === 'intl';
  const isVerify = values.mode === 'verify';
  const needsAddress = values.via === 'email' || values.via === 'sms';
  const visibleFields = getTemplateFields(catalog, values.request_type, values.bank, values.detailed) ?? [];
  const templateFields = visibleFields.length ? visibleFields : null;
  const templateTitle = isIntl
    ? `Fields the recipient will fill (${getTemplateLabel(catalog, values.request_type, values.bank)} template)`
    : 'Fields the recipient will fill';

  if (sent) {
    return (
      <Dialog
        isOpen={isOpen}
        header={isVerify ? 'Verify and request' : 'Request bank details'}
        width='760px'
        onClose={onClose}
      >
        <Box display='flex' flexDirection='column' rowGap='14px'>
          <Typography variant='body' fontWeight='semibold'>
            {needsAddress
              ? `Request sent by ${REQUEST_VIA_LABEL[sent.via]}`
              : `Link ready to paste into ${REQUEST_VIA_LABEL[sent.via]}`}
          </Typography>
          <HighlightPanel>
            <Box display='flex' alignItems='center' justifyContent='space-between' columnGap='12px'>
              <Mono>{sent.url}</Mono>
              <IconButton data-tooltip-id={'copy-link'} onClick={copy}>
                <FaCopy />
                <Tooltip id='copy-link'>Copy link</Tooltip>
              </IconButton>
            </Box>
          </HighlightPanel>
          <Typography variant='bodySmall'>
            {sent.mode === 'verify'
              ? `The recipient verifies their identity and fills the ${sent.template_label} template. You get a notification when their profile is ready. Expires in ${sent.expiry_label}.`
              : `The recipient fills the ${sent.template_label} template without an identity check. You get a notification when it is submitted. Expires in ${sent.expiry_label}.`}
          </Typography>
          <DialogButtons>
            <ButtonNew size='medium' onClick={onClose}>
              Done
            </ButtonNew>
          </DialogButtons>
        </Box>
      </Dialog>
    );
  }

  const requestTypeOptions = getRequestTypeOptions(catalog);
  const banksOptions = getBankOptions(catalog, values.request_type);

  return (
    <Dialog
      autoFocus={false}
      isOpen={isOpen}
      header={isVerify ? 'Verify and request' : 'Request bank details'}
      width='820px'
      onClose={onClose}
    >
      <FormikForm value={formik}>
        <FormikChips
          name='request_type'
          label={<b>How would you like to make the payment?</b>}
          options={requestTypeOptions}
          direction='column'
        ></FormikChips>

        {isIntl && (
          <FormikChips
            label={<b>Which bank will you add this recipient in?</b>}
            name='bank'
            options={banksOptions}
            direction='column'
            apiError={data?.error}
          ></FormikChips>
        )}

        {templateFields && (
          <HighlightPanel margin='0 0 22px'>
            <Box display='flex' justifyContent='space-between' marginBottom={'4px'}>
              <Typography variant='bodySmall' fontWeight='semibold'>
                {templateTitle}
              </Typography>
              {isDraftTemplate(catalog, values.request_type, values.bank) && (
                <Typography variant='caption'>Draft template</Typography>
              )}
            </Box>
            <OptionGrid $cols={2} $gap='2px'>
              {visibleFields.map((field, index) => (
                <Typography key={field.key} variant='bodySmall'>
                  {index + 1}. {field.label}
                </Typography>
              ))}
            </OptionGrid>
          </HighlightPanel>
        )}

        <Box>
          <OptionGrid $cols={2}>
            <FormikInput
              name='recipient_name'
              label='Recipient name*'
              placeholder='Person or company'
              autoComplete='new-field'
              apiError={data?.error}
            />
            {values.via === 'email' && (
              <FormikInput
                name='sent_to'
                label='Email address*'
                placeholder='name@company.com'
                autoComplete='new-field'
                apiError={data?.error}
              />
            )}
            {values.via === 'sms' && <FormikPhoneInput name='sent_to' label='Mobile number*' apiError={data?.error} />}
            {!needsAddress && (
              <FormikInput
                name='sent_to'
                label='Email or mobile (optional for links)'
                placeholder='For your log'
                autoComplete='new-field'
              />
            )}
          </OptionGrid>
        </Box>

        <FormikSelectableTabs
          name='audience'
          label='The bank details will be provided by'
          direction='row'
          options={[
            { value: 'recipient', label: 'Recipient', description: 'This person provides their own bank details.' },
            {
              value: 'proxy',
              label: 'Proxy',
              description: 'This person can invite the recipient, or provide the details on their behalf.',
            },
          ]}
        />
        <br />

        <FormikRadioGroup
          name='via'
          label='Send by'
          options={Object.entries(REQUEST_VIA_LABEL).map(([value, label]) => ({ value, label }))}
        />
        <br />

        <FormikRadioGroup name='expiry_code' label='Request expires after' options={REQUEST_EXPIRY_OPTIONS} />
        <br />

        <HighlightPanel>
          <Box display='flex' gap='22px' flexDirection='column'>
            <FormikSwitchToggle
              name='wire_confirmation'
              label={<b>Send the recipient a wire confirmation after you pay?</b>}
              hasErrorComponent={false}
              hint={
                <Typography variant='bodySmall' color={COLORS.grey[650]}>
                  They choose email or phone on their form. You send the confirmation or tracking number from their
                  profile.
                </Typography>
              }
            />
            {isIntl && (
              <FormikSwitchToggle
                name='detailed'
                label={<b>Detailed information needed?</b>}
                hasErrorComponent={false}
                hint={
                  <Typography variant='bodySmall' color={COLORS.grey[650]}>
                    Adds intermediary bank, local clearing code and a supporting document to the form.
                  </Typography>
                }
              />
            )}
          </Box>
        </HighlightPanel>
        <br />

        <Box display='flex' justifyContent='space-between' alignItems='center' marginTop='16px' gap={'20px'}>
          <Typography variant='bodySmall'>
            {isVerify
              ? 'Verify and request: the recipient proves identity before the form opens.'
              : 'Request: no identity check. Anyone with the link can fill the form.'}
          </Typography>
          <Box display='flex' columnGap='14px'>
            <ButtonNew size='medium' variant='secondary' onClick={onClose}>
              Cancel
            </ButtonNew>
            <ButtonNew
              size='medium'
              isLoading={isPending}
              disabled={isIntl && !values.bank}
              onClick={() => formik.handleSubmit()}
            >
              {isVerify ? 'Send verify and request' : 'Send request'}
            </ButtonNew>
          </Box>
        </Box>
      </FormikForm>
    </Dialog>
  );
};
